'use client';

import classnames from 'classnames/bind';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { TArticleId } from '@portals/help-center-core';
import {
  EAnalytics,
  ENumberOfCalls,
  WATCH_OF_INTERSECTING_DELAY_TWO_SECONDS,
} from '@/_constants/analytics';
import { useAnalytics } from '@/_hooks/use-analytics';
import { watchOfIntersecting } from '@/_utils/watch-of-intersecting';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'CardObserver';

type TProps = {
  blockName: string;
  slideId?: TArticleId;
  isIntersectingSlider?: boolean;
};

export const CardObserver = memo(function CardObserverComponent({
  blockName,
  slideId,
  isIntersectingSlider,
}: TProps) {
  const intersectionTargetRef = useRef<HTMLDivElement | null>(null);
  const [isObserve, setIsObserve] = useState(false);
  const sendEvent = useAnalytics();
  const [hasSentOnce, setHasSentOnce] = useState(false);

  const handleUnobserveCallback = useCallback(() => {
    setIsObserve(false);
  }, []);

  useEffect(() => {
    if (isObserve || hasSentOnce) return;

    const sendAnalytics = (): void => {
      setHasSentOnce(true);

      const config = {
        name: EAnalytics.SUPPLIER_LANDING_SUCCESS_STORY_SUBBLOCK_V,
        params: {
          nameSubblock: blockName,
          idArticle: slideId,
        },
      };

      sendEvent(config);
    };

    if (!isObserve) {
      watchOfIntersecting({
        element: intersectionTargetRef.current,
        callback: sendAnalytics,
        delay: WATCH_OF_INTERSECTING_DELAY_TWO_SECONDS,
        numberOfCalls: ENumberOfCalls.ONCE,
        unobserveCallback: handleUnobserveCallback,
      });

      setIsObserve(true);
    }
  }, [
    blockName,
    sendEvent,
    slideId,
    isIntersectingSlider,
    handleUnobserveCallback,
    isObserve,
    hasSentOnce,
  ]);

  useEffect(() => {
    setHasSentOnce(false);
  }, [isIntersectingSlider]);

  return <div ref={intersectionTargetRef} className={cn(BLOCK_NAME)} />;
});
